/* ==========================================================================
   Goopie Greatness — prototype behavior
   No dependencies. Motion is CSS-driven; JS only toggles state.
   ========================================================================== */

(function () {
  "use strict";

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* --- Header: transparent over hero -> solid bar, hide on scroll down --
     Two independent states, both driven from one rAF-throttled listener:

     is-stuck   past the top, so the bar takes a surface, a shadow and a
                hairline rule — the bar reads as a layer above the page
                instead of type floating on it.
     is-hidden  scrolling DOWN past the header's own height, so the bar
                slides up out of the way and gives the content the full
                viewport. Any upward scroll brings it straight back, which
                is what makes the nav feel reachable without a trip to the
                top of the page.

     The reveal is deliberately asymmetric: hiding needs sustained downward
     intent, revealing needs almost none. A small threshold on both stops
     trackpad jitter from flickering the bar. */
  const setupHeader = (el) => {
    if (!el) return;

    const HIDE_AFTER = 8; // px of downward travel before hiding
    const SHOW_AFTER = 4; // px of upward travel before revealing
    let last = window.scrollY;
    let ticking = false;

    const update = () => {
      ticking = false;
      const y = window.scrollY;
      const delta = y - last;

      el.classList.toggle("is-stuck", y > 24);

      // Never hide at the very top, and never hide what the keyboard is in.
      if (y <= el.offsetHeight || el.contains(document.activeElement)) {
        el.classList.remove("is-hidden");
      } else if (delta > HIDE_AFTER) {
        el.classList.add("is-hidden");
      } else if (delta < -SHOW_AFTER) {
        el.classList.remove("is-hidden");
      }

      // Only advance the reference point once it actually moved, so slow
      // scrolls still accumulate toward the threshold instead of stalling.
      if (Math.abs(delta) > SHOW_AFTER) last = y;
    };

    update();
    window.addEventListener(
      "scroll",
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    // A focused link inside a hidden bar is a keyboard trap.
    el.addEventListener("focusin", () => el.classList.remove("is-hidden"));
  };

  setupHeader(document.querySelector(".header"));

  /* --- Mobile takeover menu -------------------------------------------- */
  const toggle = document.querySelector(".nav-toggle");
  const mobileNav = document.querySelector(".mobile-nav");
  if (toggle && mobileNav) {
    const setOpen = (open) => {
      toggle.setAttribute("aria-expanded", String(open));
      mobileNav.classList.toggle("is-open", open);
      document.body.style.overflow = open ? "hidden" : "";
    };

    toggle.addEventListener("click", () => {
      setOpen(toggle.getAttribute("aria-expanded") !== "true");
    });

    mobileNav.addEventListener("click", (e) => {
      if (e.target.closest("a")) setOpen(false);
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") setOpen(false);
    });
  }

  /* --- Scroll reveals (once, no repeat-on-scroll-up) -------------------- */
  const revealables = document.querySelectorAll(".reveal");
  if (revealables.length) {
    if (reduceMotion || !("IntersectionObserver" in window)) {
      revealables.forEach((el) => el.classList.add("is-in"));
    } else {
      // Opt in to the hidden start state only now that we can animate out of it.
      document.documentElement.classList.add("js-reveal");
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            // Stagger items within a shared container (50-80ms).
            const delay = Number(entry.target.dataset.revealDelay || 0);
            setTimeout(() => entry.target.classList.add("is-in"), delay);
            io.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
      );
      revealables.forEach((el) => io.observe(el));
    }
  }

  /* --- Collection: filter + sort (server-rendered) ----------------------
     The prototype sorted the DOM client-side, which only works when every
     product is already on the page. Shopify paginates, so sorting has to
     happen server-side: these controls just navigate to the same collection
     with ?sort_by / ?filter.* set. The <select> and links work without JS —
     this only removes the need to press a "go" button. */
  const chipBar = document.querySelector("[data-filter-bar]");
  const sortEl = document.querySelector("[data-sort]");
  const grid = document.querySelector("[data-product-grid]");

  const goToUrl = (url) => { window.location.href = url; };

  if (sortEl) {
    sortEl.addEventListener("change", () => {
      const url = new URL(window.location.href);
      url.searchParams.set("sort_by", sortEl.value);
      url.searchParams.delete("page");
      goToUrl(url.toString());
    });
  }

  if (chipBar) {
    // Chips are real <a> links in Liquid; let them navigate normally and
    // only paint the pressed state for immediate feedback.
    chipBar.addEventListener("click", (e) => {
      const chip = e.target.closest("[data-category-filter]");
      if (!chip) return;
      chipBar.querySelectorAll("[data-category-filter]").forEach((c) =>
        c.setAttribute("aria-pressed", String(c === chip))
      );
    });
  }

  /* --- Shop: quick add + colorway swatches ------------------------------ */
  const toast = document.querySelector("[data-cart-toast]");
  const toastText = document.querySelector("[data-cart-toast-text]");
  let toastTimer;

  const showToast = (message) => {
    if (!toast || !toastText) return;
    toastText.textContent = message;
    toast.classList.add("is-visible");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove("is-visible"), 2600);
  };

  /* Momentary "Added to cart" state on the button that was clicked. The label
     is swapped rather than the element replaced, so the button keeps its size
     and the row does not reflow. aria-live is deliberately NOT used here — the
     toast already announces the add, and doing it twice is noise for a screen
     reader. Each button owns its own timer so two quick adds cannot leave one
     stuck in the success state. */
  const ADDED_MS = 1800;
  const flashAdded = (btn) => {
    const label = btn.querySelector("[data-add-label]");
    if (!label) return;
    if (btn._addedTimer) {
      clearTimeout(btn._addedTimer);
    } else {
      btn.dataset.idleLabel = label.textContent;
    }
    label.textContent = "Added to cart";
    btn.classList.add("is-added");
    btn._addedTimer = setTimeout(() => {
      label.textContent = btn.dataset.idleLabel;
      btn.classList.remove("is-added");
      btn._addedTimer = null;
    }, ADDED_MS);
  };

  /* --- Cart state (real Shopify Cart AJAX API) --------------------------
     The nav cart replaced the CONTACT link, so it reflects real cart state.
     setCartCount is the single writer; every mutation re-reads /cart.js so
     the badge can never drift from the server's view of the cart. */
  const setCartCount = (count) => {
    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      el.textContent = String(count);
      el.setAttribute("data-cart-count", String(count));
      const link = el.closest("[aria-label]");
      if (link) {
        link.setAttribute(
          "aria-label",
          `Cart, ${count} ${count === 1 ? "item" : "items"}`
        );
      }
    });
  };

  const refreshCart = () =>
    fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
      headers: { Accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (cart) setCartCount(cart.item_count);
        return cart;
      })
      .catch(() => null);

  /* Adds a variant and reports the server's error text on failure (sold out,
     quantity limits) rather than optimistically claiming success. */
  const addToCartRequest = (id, quantity = 1) =>
    fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items: [{ id: Number(id), quantity }] }),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || "Could not add to cart.");
      return data;
    });

  // Keep the badge honest on load, and after bfcache restores.
  refreshCart();
  window.addEventListener("pageshow", (e) => { if (e.persisted) refreshCart(); });

  if (grid) {
    grid.addEventListener("click", (e) => {
      // Size chip — selects only. Adding is the button's job, so a mis-tap
      // on a size can never put the wrong thing in the cart.
      const chip = e.target.closest("[data-size-select]");
      if (chip && !chip.disabled) {
        e.preventDefault();   // the card is a stretched link
        chip
          .closest(".product-card__sizes")
          .querySelectorAll("[data-size-select]")
          .forEach((s) => s.setAttribute("aria-pressed", String(s === chip)));
        return;
      }

      // Add to cart — reads whichever size is currently selected on the card.
      const add = e.target.closest("[data-quick-add]");
      if (add && !add.disabled) {
        e.preventDefault();
        const card = add.closest(".product-card");
        const picked = card.querySelector('[data-size-select][aria-pressed="true"]');
        const size = picked ? picked.dataset.size : "";
        // Variant id comes from the selected size chip, falling back to the
        // card's default variant for single-variant products.
        const variantId = picked?.dataset.variantId || add.dataset.variantId;

        if (!variantId) {
          showToast("Pick a size first.");
          return;
        }

        // Quantity comes from the card's own stepper, defaulting to 1 for
        // cards that have none (a sold-out card renders no stepper).
        const cardQty = card.querySelector("[data-card-qty-value]");
        const quantity = cardQty ? Number(cardQty.textContent) || 1 : 1;

        add.disabled = true;
        addToCartRequest(variantId, quantity)
          .then(() => refreshCart())
          .then(() => {
            showToast(
              size
                ? `Added — ${add.dataset.product} (${size})`
                : `Added — ${add.dataset.product}`
            );
            /* The toast confirms what happened; this confirms it *where the
               click was*, so the eye does not have to travel to find the
               answer. Reverts on a timer — a button stuck reading "Added"
               would lie about what the next click does. */
            flashAdded(add);
            if (cardQty) cardQty.textContent = "1";
          })
          .catch((err) => showToast(err.message))
          .finally(() => { add.disabled = false; });
        return;
      }

      // Card quantity stepper — same clamp as the PDP's.
      const step = e.target.closest("[data-card-qty] [data-step]");
      if (step) {
        e.preventDefault();
        const value = step.closest("[data-card-qty]").querySelector("[data-card-qty-value]");
        value.textContent = String(
          Math.min(10, Math.max(1, Number(value.textContent) + Number(step.dataset.step)))
        );
        return;
      }

      // Colorway — swaps the selected swatch without leaving the grid.
      const swatch = e.target.closest("[data-swatch]");
      if (swatch) {
        e.preventDefault();
        const row = swatch.closest(".product-card__colors");
        row.querySelectorAll("[data-swatch]").forEach((s) =>
          s.setAttribute("aria-pressed", String(s === swatch))
        );
      }
    });
  }

  /* --- PDP: gallery thumbnails ----------------------------------------- */
  const thumbs = document.querySelector("[data-thumbs]");
  const heroImg = document.querySelector("[data-gallery-img]");
  if (thumbs && heroImg) {
    thumbs.addEventListener("click", (e) => {
      const thumb = e.target.closest("[role='tab']");
      if (!thumb) return;
      thumbs.querySelectorAll("[role='tab']").forEach((t) =>
        t.setAttribute("aria-selected", String(t === thumb))
      );
      // All views share one placeholder asset for now; the alt tracks the view
      // so the swap is still announced to assistive tech.
      const src = thumb.querySelector("img");
      if (src) heroImg.src = src.src;
      heroImg.alt = thumb.dataset.view;
    });
  }

  /* --- PDP: size selector ---------------------------------------------- */
  const sizeGrid = document.querySelector("[data-sizes]");
  if (sizeGrid) {
    sizeGrid.addEventListener("click", (e) => {
      const size = e.target.closest("button:not([disabled])");
      if (!size) return;
      sizeGrid.querySelectorAll("button").forEach((s) =>
        s.setAttribute("aria-pressed", String(s === size))
      );
      const err = document.querySelector("[data-size-error]");
      if (err) err.textContent = "";
    });
  }

  /* --- PDP: colour selector --------------------------------------------- */
  const swatchGrid = document.querySelector("[data-colors]");
  if (swatchGrid) {
    const nameOut = document.querySelector("[data-color-name-out]");

    const paintName = (swatch) => {
      if (nameOut) nameOut.textContent = swatch ? swatch.dataset.colorName : "";
    };

    swatchGrid.addEventListener("click", (e) => {
      const swatch = e.target.closest("button:not([disabled])");
      if (!swatch) return;
      swatchGrid.querySelectorAll("button").forEach((s) =>
        s.setAttribute("aria-pressed", String(s === swatch))
      );
      paintName(swatch);
    });

    /* Select the first available colour on load, so the swatch row is never
       an unanswered question the visitor has to notice. */
    const first = swatchGrid.querySelector("button:not([disabled])");
    if (first) {
      first.setAttribute("aria-pressed", "true");
      paintName(first);
    }
  }

  /* --- PDP: quantity stepper ------------------------------------------- */
  const qty = document.querySelector("[data-qty]");
  if (qty) {
    const value = qty.querySelector("[data-qty-value]");
    // The visible stepper is a <span>; the real product form posts a hidden
    // input, so the two are kept in sync here.
    const qtyInput = document.querySelector("[data-qty-input]");
    qty.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-step]");
      if (!btn) return;
      const next = Math.min(10, Math.max(1, Number(value.textContent) + Number(btn.dataset.step)));
      value.textContent = String(next);
      if (qtyInput) qtyInput.value = String(next);
    });
  }

  /* --- PDP: add to cart (real Shopify cart) ----------------------------- */
  const addToCart = document.querySelector("[data-add-to-cart]");
  const productForm = addToCart ? addToCart.closest("form") : null;

  // Inside a real product form the submit handler below owns the add, so the
  // click handler only has to validate the size choice and mirror the variant.
  if (addToCart && !productForm) {
    addToCart.addEventListener("click", () => {
      const chosen = document.querySelector("[data-sizes] [aria-pressed='true']");
      const err = document.querySelector("[data-size-error]");
      const status = document.querySelector("[data-cart-status]");

      // Single-variant products have no size grid; fall back to the button's
      // own variant id so they can still be added.
      const hasSizes = Boolean(document.querySelector("[data-sizes]"));
      if (hasSizes && !chosen) {
        if (err) err.textContent = "Pick a size first.";
        return;
      }

      const variantId = chosen?.dataset.variantId || addToCart.dataset.variantId;
      if (!variantId) {
        if (err) err.textContent = "This item is unavailable.";
        return;
      }

      const qtyEl = document.querySelector("[data-qty-value]");
      const count = Number(qtyEl ? qtyEl.textContent : 1);
      const label = addToCart.textContent;

      addToCart.disabled = true;
      addToCart.textContent = "Adding…";

      addToCartRequest(variantId, count)
        .then(() => refreshCart())
        .then(() => {
          if (status) {
            status.textContent =
              "Added — " + count + "x" + (chosen ? " size " + chosen.textContent.trim() : "") + ".";
            status.classList.add("is-visible");
          }
        })
        .catch((e) => {
          if (status) {
            status.textContent = e.message;
            status.classList.add("is-visible");
          }
        })
        .finally(() => {
          addToCart.disabled = false;
          addToCart.textContent = label;
        });
    });
  }

  /* --- PDP: size selection drives the real product form ------------------
     Each .size chip carries its variant id. Selecting one updates the hidden
     `id` field so a no-JS submit still posts the right variant, and the
     submit handler blocks when a sized product has nothing chosen. */
  if (productForm) {
    const sizes = productForm.querySelector("[data-sizes]");
    const colors = productForm.querySelector("[data-colors]");
    const idField = productForm.querySelector("[data-variant-id]");
    const sizeErr = productForm.querySelector("[data-size-error]");

    /* With two options the right variant depends on BOTH answers, so neither
       grid can set the id on its own — each chip only knows the first variant
       carrying its own value. The full variant list is published by Liquid as
       JSON; match on the pair and fall back to the chip's own id when a
       product has only one option. */
    let variantData = [];
    const dataEl = document.querySelector("[data-variant-json]");
    if (dataEl) {
      try {
        variantData = JSON.parse(dataEl.textContent) || [];
      } catch (err) {
        variantData = [];
      }
    }

    const pressedIn = (grid) =>
      grid ? grid.querySelector('[aria-pressed="true"]') : null;

    const resolveVariant = () => {
      const sizeChip = pressedIn(sizes);
      const colorChip = pressedIn(colors);

      if (variantData.length) {
        const wantSize = sizeChip ? sizeChip.textContent.trim() : null;
        const wantColor = colorChip ? colorChip.dataset.colorName : null;

        const hit = variantData.find((v) => {
          const sizeOk = !wantSize || v.options.indexOf(wantSize) !== -1;
          const colorOk = !wantColor || v.options.indexOf(wantColor) !== -1;
          return sizeOk && colorOk;
        });
        if (hit) return String(hit.id);
      }

      /* One-option product, or no JSON: the chip's own id is correct. */
      const fallback = sizeChip || colorChip;
      return fallback ? fallback.dataset.variantId : null;
    };

    const syncVariant = () => {
      if (!idField) return;
      const id = resolveVariant();
      if (id) idField.value = id;
    };

    if (sizes) sizes.addEventListener("click", syncVariant);
    if (colors) colors.addEventListener("click", syncVariant);
    syncVariant();

    productForm.addEventListener("submit", (e) => {
      if (sizes && !pressedIn(sizes)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (sizeErr) sizeErr.textContent = "Pick a size first.";
        return;
      }
      syncVariant();
    });
  }

  /* --- Accordions ------------------------------------------------------- */
  document.querySelectorAll("[data-accordion]").forEach((acc) => {
    acc.addEventListener("click", (e) => {
      const head = e.target.closest(".accordion__head");
      if (!head) return;
      head.setAttribute(
        "aria-expanded",
        String(head.getAttribute("aria-expanded") !== "true")
      );
    });
  });

  /* --- Forms: inline validation, inline confirmation --------------------
     One rule set, used on submit and again on blur once a field has already
     been flagged. Errors appear when you leave a field you got wrong, and
     clear the moment you fix it — never while you are still typing. */

  // Messages are short and plain on purpose. A form that scolds is a form
  // people abandon.
  const fieldError = (input) => {
    /* A checkbox has a value whether or not it is ticked, so the empty-value
       test below would always pass it. Consent has to be an affirmative
       action, so check `checked` instead. */
    if (input.type === "checkbox") {
      if (!input.checked) {
        return input.dataset.requiredMessage || "Tick the box to continue.";
      }
      return "";
    }

    const value = input.value.trim();

    if (!value) {
      return input.tagName === "SELECT" ? "Pick one." : "This one's required.";
    }

    if (input.type === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      return "That email doesn't look right.";
    }

    if (input.type === "tel") {
      // Ask libphonenumber whether this is a real number for the country the
      // picker is on, rather than counting digits: length alone cannot tell
      // a valid 8-digit Norwegian number from a truncated US one.
      const field = input.closest("[data-phone-field]");
      const picker = field && field.querySelector("[data-phone-country]");
      const country = picker && picker.value ? picker.value : "US";

      if (window.libphonenumber) {
        if (!parsePhone(value, country)) return "That number doesn't look right.";
      } else {
        // No library: fall back to the digit count this used to do, so the
        // form still rejects an obviously partial number.
        const digits = value.replace(/\D/g, "").length;
        if (digits < 7 || digits > 15) return "Add a full phone number.";
      }
    }

    if (input.dataset.min || input.dataset.max) {
      const n = Number(value);
      const min = Number(input.dataset.min || -Infinity);
      const max = Number(input.dataset.max || Infinity);
      if (!/^\d+$/.test(value) || n < min || n > max) {
        return "Enter an age between " + min + " and " + max + ".";
      }
    }

    return "";
  };

  document.querySelectorAll("[data-validate-form]").forEach((form) => {
    const errorFor = (input) =>
      form.querySelector('[data-error-for="' + input.name + '"]');

    // Point the input at its own message so a screen reader reads the two
    // together. Done once, here, rather than hand-maintained in Liquid.
    form.querySelectorAll("[required]").forEach((input) => {
      const errEl = errorFor(input);
      if (!errEl) return;
      if (!errEl.id) errEl.id = "err-" + (input.id || input.name);
      input.setAttribute("aria-describedby", errEl.id);
    });

    const apply = (input) => {
      const message = fieldError(input);
      const errEl = errorFor(input);
      input.setAttribute("aria-invalid", String(Boolean(message)));
      if (errEl) errEl.textContent = message;
      return !message;
    };

    // Re-check on the way out of a field, but only once it has been flagged
    // — nobody wants an error for an email they have not finished typing.
    form.addEventListener(
      "blur",
      (e) => {
        const input = e.target;
        if (!input.matches || !input.matches("[required]")) return;
        if (input.getAttribute("aria-invalid") !== "true") return;
        apply(input);
      },
      true
    );

    // A select or checkbox is committed the moment it changes, so correct it
    // there — blur is too late for a control the visitor clicks once.
    form.addEventListener("change", (e) => {
      const input = e.target;
      if (!input.matches) return;
      if (!input.matches('select[required], input[type="checkbox"][required]')) return;
      if (input.getAttribute("aria-invalid") !== "true") return;
      apply(input);
    });

    form.addEventListener("submit", (e) => {
      let valid = true;
      form.querySelectorAll("[required]").forEach((input) => {
        if (!apply(input)) valid = false;
      });

      if (!valid) {
        // Only block the real submission when validation actually failed.
        e.preventDefault();
        form.querySelector('[aria-invalid="true"]')?.focus();
        return;
      }

      // Valid: the modal intercepts and posts over fetch; everywhere else
      // the browser POSTs to Shopify (/contact, customer forms) and Shopify
      // redirects back with ?form_type=... and renders the notice.
    });
  });

  /* --- Phone: international capture --------------------------------------
     One number field paired with a country picker, backed by
     libphonenumber-js (vendored in assets/libphonenumber.min.js).

     Why a picker rather than asking everyone to type "+44": a bare local
     number is ambiguous — 020 7183 8750 is only a London number if you
     already know the country — and most people do not type their own
     country code when giving a number at home. The picker makes the country
     explicit and lets the visitor type the number exactly as they would say
     it out loud, which is the whole point.

     Three things stay true whatever they do:
       - typing formats as-you-type using that country's real grouping
       - pasting a full +44… number moves the picker to match
       - what is submitted is always E.164 (+442071838750), so the customer
         record holds one canonical shape no matter how it was entered

     Without the library (blocked script, cached miss) every one of these
     degrades to a plain tel input that posts what was typed. That was the
     behaviour before this existed, so nothing is lost. */

  const LPN = window.libphonenumber;

  /* E.164 for a typed number, or "" when it is not a valid number for that
     country. Used both by the validator (is this real?) and on submit (what
     do we store?), so the two can never disagree about a number. */
  const parsePhone = (value, country) => {
    if (!LPN || !value) return "";
    try {
      const parsed = String(value).trim().charAt(0) === "+"
        ? LPN.parsePhoneNumberFromString(value)
        : LPN.parsePhoneNumberFromString(value, country);
      return parsed && parsed.isValid() ? parsed.number : "";
    } catch (e) {
      return "";
    }
  };

  /* Region names for the picker. Intl.DisplayNames is in every browser we
     support; where it is missing the ISO code stands in, which is still a
     usable label ("GB +44"). */
  const regionName = (() => {
    let dn = null;
    try {
      dn = new Intl.DisplayNames([document.documentElement.lang || "en"], {
        type: "region",
      });
    } catch (e) {}
    return (code) => {
      if (!dn) return code;
      try {
        return dn.of(code) || code;
      } catch (e) {
        return code;
      }
    };
  })();

  document.querySelectorAll("[data-phone-field]").forEach((field) => {
    const number = field.querySelector("[data-phone-number]");
    const select = field.querySelector("[data-phone-country]");
    const dial = field.querySelector("[data-phone-dial]");
    if (!number) return;

    /* No library: leave the plain input alone and keep the country UI out of
       the tab order entirely, rather than showing a picker that does nothing. */
    if (!LPN || typeof LPN.AsYouType !== "function") {
      field.classList.add("phone-input--plain");
      return;
    }

    const form = number.closest("form");

    /* --- Country list ---------------------------------------------------
       Built from the library's own country list so it cannot drift from the
       formatter. Sorted by display name, with the default country first so
       the common case is one keystroke away. */
    const DEFAULT_COUNTRY = "US";

    const countries = LPN.getCountries()
      .map((code) => ({
        code: code,
        dial: "+" + LPN.getCountryCallingCode(code),
        name: regionName(code),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    countries.forEach((c) => {
      const opt = document.createElement("option");
      opt.value = c.code;
      /* The dial code rides in the label so the select is self-describing
         when it is opened — a bare country name would not tell you what the
         field is about to prepend. */
      opt.textContent = c.name + " (" + c.dial + ")";
      select.appendChild(opt);
    });

    select.hidden = false;

    const dialFor = (code) => {
      try {
        return "+" + LPN.getCountryCallingCode(code);
      } catch (e) {
        return "";
      }
    };

    let country = DEFAULT_COUNTRY;

    const paintCountry = () => {
      select.value = country;
      if (dial) dial.textContent = dialFor(country);
    };

    /* --- Formatting -----------------------------------------------------
       AsYouType is stateful and formats from scratch each pass, so a fresh
       instance per keystroke is both correct and cheap. */
    const format = (value, forCountry) => {
      const typed = String(value || "");
      /* An explicit + means the visitor is stating the country themselves;
         format without a region so the library reads it from the number. */
      const ayt = typed.trim().charAt(0) === "+"
        ? new LPN.AsYouType()
        : new LPN.AsYouType(forCountry);
      return ayt.input(typed);
    };

    const reformat = () => {
      const raw = number.value;
      if (!raw) return;

      /* A pasted +xx number tells us the country — follow it, so the picker
         never contradicts what is actually in the field. */
      if (raw.trim().charAt(0) === "+") {
        const ayt = new LPN.AsYouType();
        ayt.input(raw);
        const detected = ayt.getCountry();
        if (detected && detected !== country) {
          country = detected;
          paintCountry();
        }
      }

      const caret = number.selectionStart;
      const digitsBeforeCaret = raw.slice(0, caret).replace(/\D/g, "").length;

      const next = format(raw, country);
      if (next === raw) return;

      number.value = next;

      /* Put the caret back directly after the same digit it was after
         before. Deliberately NOT skipping forward over a trailing separator:
         parking the caret after the ")" in "(309)" is what made backspace
         feel stuck, because the next keypress then aimed at punctuation
         instead of a digit. Separators the visitor types *through* are
         skipped naturally on the next keystroke. */
      let seen = 0;
      let pos = next.length;
      for (let i = 0; i < next.length; i += 1) {
        if (/\d/.test(next[i])) {
          seen += 1;
          if (seen === digitsBeforeCaret) {
            pos = i + 1;
            break;
          }
        }
      }
      if (digitsBeforeCaret === 0) {
        const first = next.search(/\d/);
        pos = first === -1 ? next.length : first;
      }

      try {
        number.setSelectionRange(pos, pos);
      } catch (err) {
        /* Some browsers refuse setSelectionRange on type="tel"; the value is
           still right, only the caret is not restored. */
      }
    };

    /* --- Deleting -------------------------------------------------------
       The formatter re-adds separators the moment they are deleted, so a
       plain backspace over one is a no-op the visitor experiences as a stuck
       field: at "(309)" the caret sits after ")", backspace removes ")",
       reformat puts it straight back, and the area code can never be cleared.

       Intercept the delete before it happens and take out the nearest digit
       instead — that is what the visitor meant. The separator then falls away
       on its own when the digit behind it is gone. */
    /* Re-format after a corrected delete, keeping the caret on the digit
       boundary the deletion left behind rather than pushing it to the end. */
    const applyDeletion = (rawAfterCut, cutIndex) => {
      const digitsBefore = rawAfterCut.slice(0, cutIndex).replace(/\D/g, "").length;
      const next = format(rawAfterCut, country);
      number.value = next;

      let seen = 0;
      let pos = 0;
      if (digitsBefore === 0) {
        pos = 0;
      } else {
        for (let i = 0; i < next.length; i += 1) {
          if (/\d/.test(next[i])) {
            seen += 1;
            if (seen === digitsBefore) {
              pos = i + 1;
              break;
            }
          }
        }
      }

      try {
        number.setSelectionRange(pos, pos);
      } catch (err) {}
    };


    number.addEventListener("beforeinput", (e) => {
      const back = e.inputType === "deleteContentBackward";
      const fwd = e.inputType === "deleteContentForward";
      if (!back && !fwd) return;

      const start = number.selectionStart;
      const end = number.selectionEnd;
      /* A selection deletes exactly what is selected; only a collapsed caret
         needs the separator skipped. */
      if (start !== end) return;

      const value = number.value;
      const isDigit = (ch) => /\d/.test(ch || "");

      if (back) {
        /* Walk left past separators to the digit the visitor is aiming at. */
        let i = start - 1;
        while (i >= 0 && !isDigit(value[i])) i -= 1;
        if (i < 0) return;
        /* Already a digit directly behind the caret: nothing to correct. */
        if (i === start - 1) return;

        e.preventDefault();
        const cut = value.slice(0, i) + value.slice(i + 1);
        applyDeletion(cut, i);
      } else {
        let i = start;
        while (i < value.length && !isDigit(value[i])) i += 1;
        if (i >= value.length) return;
        if (i === start) return;

        e.preventDefault();
        const cut = value.slice(0, i) + value.slice(i + 1);
        applyDeletion(cut, i);
      }
    });

    number.addEventListener("input", reformat);
    /* Paste and autofill do not always raise `input` — catch them on change. */
    number.addEventListener("change", reformat);

    /* Changing the country re-groups whatever is already typed, so the field
       is never left showing one country's spacing under another's flag. */
    select.addEventListener("change", () => {
      country = select.value;
      if (dial) dial.textContent = dialFor(country);
      if (number.value) {
        const digits = number.value.replace(/^\+/, "").replace(/\D/g, "");
        number.value = format(digits, country);
      }
      number.focus();
    });

    /* --- Submission -----------------------------------------------------
       Shopify stores contact[phone] verbatim, so normalise to E.164 on the
       way out. The visible field keeps its readable grouping; a hidden twin
       carries the canonical value and the visible one is taken out of the
       POST so only one phone lands on the record. */
    if (form) {
      form.addEventListener(
        "submit",
        () => {
          const parsed = parsePhone(number.value, country);
          if (!parsed) return;

          let hidden = form.querySelector("[data-phone-e164]");
          if (!hidden) {
            hidden = document.createElement("input");
            hidden.type = "hidden";
            hidden.setAttribute("data-phone-e164", "");
            form.appendChild(hidden);
          }
          hidden.name = number.name;
          hidden.value = parsed;
          /* Disabled rather than renamed: the typed value survives for a
             second pass if the post comes back with errors. */
          number.disabled = true;

          /* The modal posts over fetch and stays on the page, so put the
             field back once the payload has been built. */
          window.setTimeout(() => {
            number.disabled = false;
          }, 0);
        },
        /* Late, so validation and the note-packing handler have both run. */
        false
      );
    }

    /* Format whatever is already in the field — Shopify re-rendering the
       form after a failed post, or a browser restoring a session. */
    paintCountry();
    if (number.value) {
      if (number.value.trim().charAt(0) === "+") {
        const ayt = new LPN.AsYouType();
        ayt.input(number.value);
        const detected = ayt.getCountry();
        if (detected) {
          country = detected;
          paintCountry();
        }
      }
      number.value = format(number.value, country);
    }
  });

  /* --- Join the Movement: fold the non-customer fields into the note -----
     Shopify's customer form persists first_name, last_name, email and phone
     as real customer fields. City, state and age are not, so they would be silently
     dropped on POST. Pack them into contact[note] (which shows on the
     customer record) and mirror the state into contact[tags] so the admin
     can segment the launch list by region.

     The fields are found by data-movement-field, never by name: the packing
     step disables them so they stay out of the POST, and a name-based lookup
     would then read blank if the visitor submits twice (a failed validation
     pass, say). Disabling preserves the typed value; renaming would not. */
  document.querySelectorAll("[data-movement-form]").forEach((form) => {
    const extras = ["city", "state", "age"];

    form.addEventListener("submit", (e) => {
      // The validation handler above runs first and calls preventDefault when
      // a field is empty. Packing must not run on that pass: disabling the
      // inputs would leave the visitor unable to fill in the very fields the
      // errors just asked them for.
      if (e.defaultPrevented) return;

      const read = (key) => {
        const el = form.querySelector('[data-movement-field="' + key + '"]');
        return el ? el.value.trim() : "";
      };

      const city = read("city");
      const state = read("state");
      const age = read("age");

      /* The short (modal) cut asks for one Name. Shopify wants first_name and
         last_name as separate customer fields, so split on the first space:
         everything before it is the first name, the remainder is the last.
         "Mary Jo Van Dyke" -> "Mary" + "Jo Van Dyke", which is wrong for some
         names but keeps the record addressable; a mononym leaves last blank
         rather than duplicating. The full form has real first/last inputs and
         never reaches this branch. */
      const nameEl = form.querySelector('[data-movement-field="name"]');
      if (nameEl) {
        const whole = nameEl.value.trim().replace(/\s+/g, " ");
        const cut = whole.indexOf(" ");
        const first = cut === -1 ? whole : whole.slice(0, cut);
        const last = cut === -1 ? "" : whole.slice(cut + 1);

        // Reuse the hidden twins across submits, same as the note below, so a
        // second pass updates them instead of appending duplicates.
        [["first_name", first], ["last_name", last]].forEach(([key, val]) => {
          let hid = form.querySelector('[data-movement-name="' + key + '"]');
          if (!hid) {
            hid = document.createElement("input");
            hid.type = "hidden";
            hid.name = "contact[" + key + "]";
            hid.setAttribute("data-movement-name", key);
            form.appendChild(hid);
          }
          hid.value = val;
        });
        nameEl.disabled = true;
      }

      extras.forEach((key) => {
        const el = form.querySelector('[data-movement-field="' + key + '"]');
        if (el) el.disabled = true;
      });

      // Reuse the hidden input across submits so a second pass updates the
      // note instead of appending a second contact[note] to the payload.
      let note = form.querySelector('[data-movement-note]');
      if (!note) {
        note = document.createElement("input");
        note.type = "hidden";
        note.name = "contact[note]";
        note.setAttribute("data-movement-note", "");
        form.appendChild(note);
      }
      /* Shopify's customer form accepts contact[phone] but does not persist
         it, so the number never reached the customer record. Carry it in the
         note with the other non-native fields rather than losing it. The
         phone handler above has already written the E.164 value into its
         hidden twin by this point; fall back to whatever is typed if that
         handler did not run. */
      const e164 = form.querySelector("[data-phone-e164]");
      const typed = form.querySelector("[data-phone-number]");
      let phone = "";
      if (e164 && e164.value) {
        phone = e164.value;
      } else if (typed && typed.value) {
        phone = typed.value.trim();
      }

      let noteText = "";
      if (phone) noteText += "Phone: " + phone + " | ";
      noteText += "City: " + city + " | State: " + state + " | Age: " + age;
      note.value = noteText;

      // Rebuild from the base tags each time, for the same reason.
      const tags = form.querySelector('[name="contact[tags]"]');
      if (tags) {
        if (!tags.dataset.baseTags) tags.dataset.baseTags = tags.value;
        tags.value = tags.dataset.baseTags;
        if (state) {
          tags.value += ",state-" + state.toLowerCase().replace(/\s+/g, "-");
        }
      }
    });
  });
})();
